import { deserialise } from 'kitsu-core';
import { action, observable, computed, runInAction } from 'mobx';

// import { AvailableCoffeLot } from 'types/model/lot';
import AvailableExcessOrOrphanItemModel from 'availableExcessOrOrphanCoffee/models/AvailableExcessOrOrphanItemModel';

import { OrphanStatus } from 'availableExcessOrOrphanCoffee/containers/AvailableExcessOrOrphanCoffeeContainer';
import SelectOptionArray from 'types/model/selectOption';

class AvailableExcessOrOrphanCoffeeStore {
  public items = observable.array<AvailableExcessOrOrphanItemModel>();
  public missingWeight: string;
  public millingOrderId: number;
  public orphanStatus: OrphanStatus;
  public qualities: SelectOptionArray;

  constructor(
    items: Array<any> = [],
    missingWeight: string,
    millingOrderId: number,
    orphanStatus: OrphanStatus,
    qualities: SelectOptionArray
  ) {
    let newItems = Array<AvailableExcessOrOrphanItemModel>();
    const deserialisedData = deserialise(items).data;

    if (deserialisedData) {
      newItems = deserialisedData.map(
        (item: any) => new AvailableExcessOrOrphanItemModel(item)
      );
    }

    this.missingWeight = missingWeight;
    this.millingOrderId = millingOrderId;
    this.orphanStatus = orphanStatus;
    this.qualities = qualities;
    this.items = observable.array(newItems);
  }

  @action public toggleLotSelection = (item: AvailableExcessOrOrphanItemModel) => {
    item.setSelected(!item.selected);
  };

  @computed get selectedItems(): Array<AvailableExcessOrOrphanItemModel> {
    return observable.array(this.items.filter((item) => item.selected));
  }

  @computed get selectedWeight(): string {
    return this.selectedItems
      .reduce((currentWeight, item) => currentWeight + parseFloat(item.quantity), 0.0)
      .toFixed(2);
  }

  @computed get openWeight(): string {
    return (parseFloat(this.missingWeight) - parseFloat(this.selectedWeight)).toFixed(2);
  }

  @action public loadTemporarySelections = async () => {
    try {
      const response = await fetch(
        `/temporary_milling_coffes_selections/${this.millingOrderId}`,
        {
          method: 'GET',
        }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch temporary selections');
      }

      const data = await response.json();

      runInAction(() => {
        if (data.selected_items && data.selected_items.length > 0) {
          const uniqueItems = new Set();
          const normalizedItems = data.selected_items.flatMap((item) => {
            const items = Array.isArray(item) ? item : [item];
            return items.filter((subItem) => {
              const uniqueKey = subItem.id;
              if (uniqueItems.has(uniqueKey)) {
                return false;
              }
              uniqueItems.add(uniqueKey);
              return true;
            });
          });
          normalizedItems.forEach((tempItem: any) => {
            const existingItem = this.items.find(
              (item) => item.attributes.id === tempItem.id
            );
            if (existingItem) {
              // Actualiza el item existente con los datos temporales
              existingItem.setQuantity(tempItem.quantity);
              existingItem.setSelected(tempItem.selected);
            } else {
              // Agrega un nuevo item desde los datos temporales
              this.items.push(new AvailableExcessOrOrphanItemModel(tempItem));
            }
          });
        }
      });
    } catch (error) {
      console.error('Error loading temporary selections:', error);
    }
  };

  /**
   * Valida y carga los items disponibles cuando no hay registros temporales.
   */
  @action public validateAndLoadAvailableItems = async () => {
    try {
      const response = await fetch(
        `/temporary_milling_coffes_selections/exclude?exclude_milling_order_id=${this.millingOrderId}`,
        {
          method: 'GET',
        }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch available items');
      }

      const data = await response.json();
      if (!data.items || data.items.length === 0) {
        return;
      }

      runInAction(() => {
        const reservedMap = new Map<number, number>();

        data.items.forEach((selection: any) => {
          selection.selected_items.forEach((selectedItem: any) => {
            const id = selectedItem.id;
            const quantity = parseFloat(selectedItem.quantity);

            if (!reservedMap.has(id)) {
              reservedMap.set(id, 0);
            }

            reservedMap.set(id, reservedMap.get(id)! + quantity);
          });
        });

        this.items.forEach((item) => {
          const reserved = reservedMap.get(item.attributes.id) || 0;
          const originalAvailable = parseFloat(
            item.attributes.available_weight.toString()
          );

          const newAvailable = originalAvailable - reserved;
          const clampedAvailable =
            newAvailable <= 0 ? 0 : parseFloat(newAvailable.toFixed(2));

          item.setFinalAvailableWeight(clampedAvailable);

          // "Cantidad a utilizar" must reflect the real available (net of other milling
          // orders' reservations), not the original serializer weight. Clamp it down so it
          // never shows/defaults to more than what's actually available.
          if (parseFloat(item.quantity) > clampedAvailable) {
            item.setQuantity(clampedAvailable.toFixed(2));
          }
        });

        this.items.replace(this.items.filter((item) => item.finalAvailableWeight > 0));
      });
    } catch (error) {
      console.error('Error validating and loading available items:', error);
    }
  };
}

export default AvailableExcessOrOrphanCoffeeStore;
